import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, Plus, Trash2, Edit2, Loader2, Info, AlertTriangle, Check, Download } from 'lucide-react';

function formatDtg(unix, tz, dateOnly) {
  // OL game epochs are "floating-local" (wall-clock encoded at UTC offset 0).
  // Render at UTC so the displayed digits ARE the game's local time — matches
  // the website. Do NOT convert to America/New_York. See /app/docs/OL_TIMEZONE.md.
  if (!unix) return '—';
  const opts = dateOnly
    ? { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }
    : { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
  try {
    return new Date(unix * 1000).toLocaleString('en-US', opts);
  } catch {
    const { timeZone, ...noTz } = opts;
    return new Date(unix * 1000).toLocaleString('en-US', noTz);
  }
}

// Canonical time_tba -> "TBD"/"" per OL_TIMEZONE §5A (mirrors backend time_tba_text).
function timeTbaText(row) {
  if (!row) return '';
  const v = row.time_tba;
  const u = Number(row.game_unix || row.unix_dtg || 0);
  if (v === true || v === 1 || (typeof v === 'string' && v.trim() === '1') || (u > 0 && u % 86400 === 0)) return 'TBD';
  if (typeof v === 'string' && v.trim()) return v.trim();
  return '';
}

// Unix-seconds <-> "datetime-local" string, browser-INDEPENDENT (offset 0).
// The stored epoch's UTC digits are the game's local wall clock, so read/write
// them via getUTC*/Date.UTC — never new Date(localStr) (browser tz).
function unixToLocalInput(unix) {
  if (!unix) return '';
  const d = new Date(unix * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}
function localInputToUnix(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  return Math.floor(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) / 1000);
}

// Decide whether a column supports inline editing. Image / thumbnail /
// link / readonly columns route the user to the modal so the existing
// uploader / picker UI is reused — per the user's choice ("keep the
// modal as a fallback when an image field is touched").
function isCellInlineEditable(col) {
  if (col.inlineEditable === false) return false;
  if (col.readOnly) return false;
  if (!col.key || col.key === 'id' || col.key === 'wp_post_id') return false;
  if (col.type === 'thumbnail') return false;
  if (col.type === 'link') return false;
  if (col.type === 'computed_result') return false;
  return true;
}

/* CellValue — read-only display of a cell. Honours pending unsaved
   edits by overlaying the user's queued draft value on top of the
   row's server value. The amber border / italic styling is rendered
   by the EditableCell wrapper (this layer just resolves the value). */
function CellValue({ col, row, entities, siteTimezone }) {
  const val = col.render ? col.render(row) : row[col.key];

  // If `col.render` returned a ReactElement, use it verbatim — don't let
  // the downstream type-based renderers below coerce it to `[object Object]`.
  if (React.isValidElement(val)) return val;

  if (col.type === 'thumbnail') {
    if (!val) return <div className="h-7 w-7 rounded bg-slate-600/50" />;
    return <img src={val} alt="" className="h-7 w-7 rounded object-cover bg-slate-700" onError={e => { e.target.style.display = 'none'; }} />;
  }
  if (col.type === 'link') {
    if (!val) return <span className="text-slate-500">—</span>;
    try { return <a href={val} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 text-xs truncate max-w-[120px] block" onClick={e => e.stopPropagation()} title={val}>{new URL(val).hostname}</a>; }
    catch { return <span className="text-xs text-slate-400 truncate max-w-[120px] block">{val}</span>; }
  }
  if (col.type === 'datetime') {
    const tba = timeTbaText(row);
    return <span className="text-xs text-slate-100 whitespace-nowrap">{tba ? `${formatDtg(val, siteTimezone, true)} ${tba}` : formatDtg(val, siteTimezone)}</span>;
  }
  if (col.type === 'boolean') return val ? <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">Yes</Badge> : <span className="text-slate-400 text-xs">No</span>;
  if (col.type === 'homeaway') return <Badge variant="outline" className={`text-[10px] ${val ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-blue-500/10 text-blue-400 border-blue-500/30'}`}>{val ? 'H' : 'A'}</Badge>;
  if (col.type === 'slug') return <code className="text-emerald-400 text-xs font-mono">{val || ''}</code>;

  // Resolve entity references to names
  if (col.resolve && entities) {
    const list = entities[col.resolve.entity] || [];
    const titleField = col.resolve.field || 'title';
    const match = list.find(e => String(e.id) === String(val));
    if (match) return <span className="text-slate-100">{match[titleField] || match.title || match.name || val}</span>;
    if (!val || val === -1 || val === 0 || val === '-1' || val === '0') return <span className="text-slate-500">—</span>;
    return <span className="text-slate-400">{val}</span>;
  }

  if (val === null || val === undefined || val === '' || val === -1 || val === 0) return <span className="text-slate-500">—</span>;
  return <span className={`truncate ${col.maxWidthClass || 'max-w-[200px]'} block text-slate-100`} title={String(val)}>{String(val)}</span>;
}

function Tooltip({ text, children }) {
  return (
    <span className="relative group/tip inline-flex items-center gap-1">
      {children}
      {text && (
        <>
          <Info className="h-3 w-3 text-slate-500 group-hover/tip:text-amber-400 cursor-help" />
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-700 text-slate-100 text-[10px] rounded shadow-lg whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none z-50">
            {text}
          </span>
        </>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// EditableCell — inline-edit per column, save on blur.
//
// Constraints (from product owner, Apr 28):
//   * Each cell becomes individually editable on click (no row-level
//     edit-mode toggle).
//   * Save on blur only — no debounce. Pressing Enter commits + blurs;
//     Escape reverts.
//   * Image / link / readonly columns aren't inline-editable — clicking
//     them opens the existing modal via the row's pencil button (kept
//     as the fallback for complex / multi-section edits).
//   * Validation surfaces in the row's footer error bar; the cell
//     itself just stays in edit mode if the parent reports an error
//     for its key, with a red ring so the user sees the offender.
// ---------------------------------------------------------------------------
function EditableCell({ col, row, entities, siteTimezone, onQueue, getQueuedValue, errorKey, onOpenModal, archiveReadOnly }) {
  // The effective value for this cell — either the user's queued
  // unsaved draft or whatever the row currently carries.
  const queuedValue = getQueuedValue?.(row, col.key);
  const hasQueued = queuedValue !== undefined;
  const baseValue = hasQueued ? queuedValue : row[col.key];

  // Synthetic row with the queued value applied so the read-only
  // CellValue (resolve/format/badge logic) renders the user's pending
  // edit instead of the stale server value.
  const displayRow = hasQueued ? { ...row, [col.key]: queuedValue } : row;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => baseValue);
  const inputRef = useRef(null);
  const cancelledRef = useRef(false);

  // When the upstream row changes (e.g. another cell saved and the row
  // refetched), keep our draft synced as long as the user isn't
  // actively editing this cell.
  useEffect(() => {
    if (!editing) setDraft(baseValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, col.key, hasQueued, queuedValue, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Select for text inputs so the user can immediately type over.
      try {
        if (typeof inputRef.current.select === 'function') inputRef.current.select();
      } catch { /* selects only work on text-ish inputs */ }
    }
  }, [editing]);

  const rowReadOnly = archiveReadOnly && (row._archived === true || (row._source && row._source !== 'current'));
  const inlineEditable = rowReadOnly ? false : isCellInlineEditable(col);
  const hasError = !!errorKey;

  // Click handler — what happens when the user clicks the cell.
  const handleCellClick = (e) => {
    e.stopPropagation();
    if (!inlineEditable) {
      // Archive rows are read-only (their edit would 404) — do nothing.
      if (rowReadOnly) return;
      // Image / readonly / computed cells fall through to the modal so
      // the existing image uploader / picker is reused.
      onOpenModal?.(row);
      return;
    }
    cancelledRef.current = false;
    setDraft(baseValue);
    setEditing(true);
  };

  const commit = () => {
    if (cancelledRef.current) {
      setEditing(false);
      return;
    }
    const orig = row[col.key];
    // No-op: nothing changed from the server value.
    if (draft === orig || (draft === '' && (orig === null || orig === undefined))) {
      onQueue?.(row, col.key, undefined);  // clear any prior queued change
      setEditing(false);
      return;
    }
    // Queue (don't push) — user explicitly hits Save in the floating
    // bar to flush. This is the central change vs. the legacy
    // save-on-blur model: edits accumulate, the pill stays green, and
    // the user explicitly commits the burst.
    onQueue?.(row, col.key, draft);
    setEditing(false);
  };

  const cancel = () => {
    cancelledRef.current = true;
    setDraft(baseValue);
    setEditing(false);
  };

  // Read-only render: show the value, but also surface a save-error
  // ring so the user can find the offending cell without scanning the
  // bottom error bar.
  if (!editing) {
    const wrapClass = `${inlineEditable ? 'cursor-text hover:bg-slate-700/40' : 'cursor-pointer'} -mx-1 px-1 rounded transition-colors ${hasError ? 'ring-1 ring-red-500/60 bg-red-900/15' : ''} ${hasQueued ? 'bg-amber-500/15 ring-1 ring-amber-500/40 italic' : ''}`;
    return (
      <div className={wrapClass}
           onClick={handleCellClick}
           data-testid={`cell-${col.key}-${row.id}`}
           data-inline-editable={inlineEditable ? 'true' : 'false'}
           title={hasQueued ? 'Unsaved change — click "Save N" to push to WP' : undefined}>
        <CellValue col={col} row={displayRow} entities={entities} siteTimezone={siteTimezone} />
        {hasQueued && <span className="inline-block h-1.5 w-1.5 ml-1 rounded-full bg-amber-400 align-middle" aria-label="unsaved" />}
      </div>
    );
  }

  // Edit render — shape of the input depends on column type.
  const baseInput = `h-7 text-xs bg-slate-900 border-amber-500/60 text-slate-100 ${hasError ? 'border-red-500/80 ring-1 ring-red-500/40' : ''}`;
  const stop = (e) => e.stopPropagation();
  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  // Boolean cells flip on click (no inline checkbox dance — toggle and
  // commit immediately). We never enter the editing-true state for
  // booleans; the click is the commit.
  if (col.type === 'boolean' || col.type === 'homeaway') {
    // We won't reach here in practice — handled in the read branch.
    return null;
  }

  // Select: dropdown of options.
  if (col.type === 'select' && Array.isArray(col.options)) {
    return (
      <select
        ref={inputRef}
        value={draft ?? ''}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKey}
        onClick={stop}
        disabled={false}
        className={`${baseInput} rounded-md px-1`}
        data-testid={`cell-input-${col.key}-${row.id}`}
      >
        {col.options.map(opt => (
          <option key={opt.v ?? opt.value ?? ''} value={opt.v ?? opt.value ?? ''}>{opt.l ?? opt.label ?? opt.v ?? opt.value}</option>
        ))}
      </select>
    );
  }

  // Entity picker — when the column is rendered via `resolve`, edit
  // it as a select of the available entities.
  if (col.resolve && entities) {
    const list = entities[col.resolve.entity] || [];
    return (
      <select
        ref={inputRef}
        value={draft ?? ''}
        onChange={(e) => setDraft(e.target.value === '' ? null : Number(e.target.value) || e.target.value)}
        onBlur={commit}
        onKeyDown={onKey}
        onClick={stop}
        disabled={false}
        className={`${baseInput} rounded-md px-1 max-w-[180px]`}
        data-testid={`cell-input-${col.key}-${row.id}`}
      >
        <option value="">—</option>
        {list.map(e => (
          <option key={e.id} value={e.id}>{e.title || e.name || e.slug || e.id}</option>
        ))}
      </select>
    );
  }

  // `editAs` entity picker — inline-edit a resolved column (e.g. Home Venue)
  // as a select that WRITES a chosen entity's canonical value (default the OL
  // uuid via `valueField:'ol_id'`) into `col.key`. Keeps the grid and the modal
  // form on ONE stored format so the resolved id never leaks back as a number.
  if (col.editAs && col.editAs.resolve && entities) {
    const ea = col.editAs;
    const list = entities[ea.resolve.entity] || [];
    const vf = ea.valueField || 'ol_id';
    const tf = ea.resolve.field || 'title';
    return (
      <select
        ref={inputRef}
        value={draft ?? ''}
        onChange={(e) => setDraft(e.target.value === '' ? '' : e.target.value)}
        onBlur={commit}
        onKeyDown={onKey}
        onClick={stop}
        disabled={false}
        className={`${baseInput} rounded-md px-1 max-w-[200px]`}
        data-testid={`cell-input-${col.key}-${row.id}`}
      >
        <option value="">—</option>
        {list.map(e => (
          <option key={e.id} value={e[vf] ?? e.id}>{e[tf] || e.title || e.name || e.slug || e.id}</option>
        ))}
      </select>
    );
  }

  if (col.type === 'datetime') {
    return (
      <Input
        ref={inputRef}
        type="datetime-local"
        value={typeof draft === 'string' ? draft : unixToLocalInput(draft)}
        onChange={(e) => setDraft(localInputToUnix(e.target.value))}
        onBlur={commit}
        onKeyDown={onKey}
        onClick={stop}
        disabled={false}
        className={baseInput}
        data-testid={`cell-input-${col.key}-${row.id}`}
      />
    );
  }

  if (col.type === 'number') {
    return (
      <Input
        ref={inputRef}
        type="number"
        value={draft ?? ''}
        onChange={(e) => setDraft(e.target.value === '' ? null : Number(e.target.value))}
        onBlur={commit}
        onKeyDown={onKey}
        onClick={stop}
        disabled={false}
        className={baseInput}
        data-testid={`cell-input-${col.key}-${row.id}`}
      />
    );
  }

  // Default: text / slug.
  return (
    <Input
      ref={inputRef}
      value={draft ?? ''}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={onKey}
      onClick={stop}
      disabled={false}
      className={`${baseInput} ${col.type === 'slug' ? 'font-mono text-emerald-400' : ''}`}
      data-testid={`cell-input-${col.key}-${row.id}`}
    />
  );
}

// Booleans toggle on a single click — we don't need the input dance.
// Queues the flip into the table-level pendingChanges Map; the
// floating Save bar flushes the burst.
function ToggleCell({ col, row, onToggle, queuedValue, errorKey }) {
  const inlineEditable = isCellInlineEditable(col);
  const hasQueued = queuedValue !== undefined;
  const serverVal = row[col.displayKey || col.key];
  const val = hasQueued ? queuedValue : serverVal;
  const handleClick = (e) => {
    e.stopPropagation();
    if (!inlineEditable) return;
    // Re-toggling back to the original server value clears the queue
    // entry — keeps the "N changes" counter accurate.
    const next = !val;
    onToggle?.(next === serverVal ? undefined : next);
  };
  const ring = errorKey
    ? 'ring-1 ring-red-500/60'
    : hasQueued
      ? 'ring-1 ring-amber-500/50 bg-amber-500/15'
      : '';
  return (
    <button type="button"
            onClick={handleClick}
            className={`${ring} hover:bg-slate-700/40 -mx-1 px-1 py-0.5 rounded transition-colors`}
            data-testid={`cell-toggle-${col.key}-${row.id}`}
            title={hasQueued
              ? 'Unsaved change — click "Save N" to push to WP'
              : (inlineEditable ? `Click to toggle (${val ? 'currently yes' : 'currently no'})` : '')}>
      {col.type === 'homeaway'
        ? <Badge variant="outline" className={`text-[10px] ${val ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-blue-500/10 text-blue-400 border-blue-500/30'}`}>{val ? 'H' : 'A'}</Badge>
        : (val ? <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">{col.yesLabel || 'Yes'}</Badge> : <span className="text-slate-400 text-xs">{col.noLabel || 'No'}</span>)}
      {hasQueued && <span className="inline-block h-1.5 w-1.5 ml-1 rounded-full bg-amber-400 align-middle" aria-label="unsaved" />}
    </button>
  );
}

export default function DataTable({
  data = [], columns = [], loading, searchTerm, onSearchChange,
  page = 1, totalPages = 1, onPageChange,
  onRowEdit, onRowDelete, onNew, onRowPull,
  entityLabel, onSort, sortKey, sortDir, entities, siteTimezone, inlineFilters = null,
  // Bulk-delete (Session 24 round 6, May 12 2026): when `onBulkDelete`
  // is provided the table renders a 1st-column checkbox per row, a
  // select-all-on-page checkbox in the header, and a contextual action
  // bar above the table that surfaces the selected count + "Delete N
  // selected" + Clear buttons. The handler receives an array of full
  // row objects so the caller can resolve IDs however it likes.
  onBulkDelete,                 // async (rows[]) -> void; throw on partial failure
  onBulkPull,                   // async (rows[]) -> void; scoped "pull selected from WP"
  // Inline edit. Both optional — when omitted the table behaves like
  // the legacy modal-only flow (cells are read-only, row click opens
  // the modal).
  onCellSave,                   // async (row, colKey, newValue) -> void; per-cell fallback (queued model below preferred)
  onBulkSave,                   // async (patches[]) -> void; receives [{ row, fields }] for the floating Save flow
  // Staff tab: archive rows (`_archived`/`_source!=='current'`) live in a
  // read-only historical collection, so editing/deleting them 404s. When this
  // flag is on, those rows render read-only (no inline edit, no pencil/delete).
  archiveReadOnly = false,
}) {
  // Save state for the floating "Save N" bar.
  const [errorMap, setErrorMap] = useState({});    // { `${rowId}:${colKey}`: 'message' }

  // QUEUED-EDIT MODEL (Session 24 round 8, May 12 2026):
  // Inline-edit cells no longer push on blur — they enqueue the
  // user's draft into `pendingChanges` so the user explicitly hits a
  // single floating "Save N changes" button to flush. Mirrors how a
  // spreadsheet feels and matches the user's mental model: "I'm
  // making a batch of edits, then committing them."
  //
  // Map shape: { `${rowId}:${colKey}`: { row, colKey, value, original } }
  // Keyed by both ids so the same row can have N queued field edits
  // without clobbering each other.
  const [pendingChanges, setPendingChanges] = useState(() => new Map());
  const [bulkSaving, setBulkSaving] = useState(false);
  // Scoped per-row "Pull this from WP" — tracks which row is currently
  // pulling so we can show a spinner without a table-wide busy flag.
  const [pullingId, setPullingId] = useState(null);
  const handleRowPull = async (row) => {
    if (!onRowPull) return;
    setPullingId(row.id);
    try { await onRowPull(row); } finally { setPullingId(null); }
  };

  const queueChange = useCallback((row, colKey, value) => {
    setPendingChanges(prev => {
      const next = new Map(prev);
      const k = `${row.id}:${colKey}`;
      if (value === undefined) {
        next.delete(k);
      } else {
        next.set(k, { row, colKey, value, original: row[colKey] });
      }
      return next;
    });
    // Any prior save error for this cell is now stale — the user
    // edited again.
    setErrorMap(prev => {
      const k = `${row.id}:${colKey}`;
      if (!(k in prev)) return prev;
      const { [k]: _gone, ...rest } = prev;
      return rest;
    });
  }, []);

  const getQueuedValue = useCallback((row, colKey) => {
    const entry = pendingChanges.get(`${row.id}:${colKey}`);
    return entry ? entry.value : undefined;
  }, [pendingChanges]);

  const discardChanges = () => {
    if (pendingChanges.size === 0) return;
    if (!window.confirm(`Discard ${pendingChanges.size} unsaved change${pendingChanges.size === 1 ? '' : 's'}?`)) return;
    setPendingChanges(new Map());
    setErrorMap({});
  };

  const saveChanges = async () => {
    if (pendingChanges.size === 0) return;
    setBulkSaving(true);
    setErrorMap({});
    // Group per row so the API call merges all field changes into a
    // single PATCH. Cheap, and avoids racy mid-burst refetches.
    const byRow = new Map();  // rowId -> { row, fields }
    pendingChanges.forEach(({ row, colKey, value }) => {
      const cur = byRow.get(row.id) || { row, fields: {} };
      cur.fields[colKey] = value;
      byRow.set(row.id, cur);
    });
    const patches = Array.from(byRow.values());
    try {
      if (onBulkSave) {
        // Preferred: caller batches & handles concurrency + refetch.
        await onBulkSave(patches);
      } else if (onCellSave) {
        // Backwards-compatible: flush one cell at a time.
        for (const { row, fields } of patches) {
          for (const [colKey, value] of Object.entries(fields)) {
            try {
              await onCellSave(row, colKey, value);
            } catch (err) {
              const msg = err?.response?.data?.detail || err?.message || 'Save failed';
              setErrorMap(prev => ({ ...prev, [`${row.id}:${colKey}`]: String(msg).slice(0, 240) }));
            }
          }
        }
      }
      // Drop changes that didn't end up in errorMap.
      setPendingChanges(prev => {
        const next = new Map();
        prev.forEach((entry, k) => {
          if (errorMap[k]) next.set(k, entry);
        });
        return next;
      });
    } catch (err) {
      // onBulkSave threw — surface the message; keep queue intact so the
      // user can fix and retry without losing their drafts.
      const msg = err?.response?.data?.detail || err?.message || 'Save failed';
      window.alert(`Save failed: ${msg}`);
    } finally {
      setBulkSaving(false);
    }
  };

  // Bulk-delete state — Set keyed by row.ol_id (or row.id when ol_id is
  // absent) so selections survive cell-edit re-renders that may shuffle
  // the row order.
  const selectable = !!onBulkDelete || !!onBulkPull;
  const rowKeyOf = (row) => row.ol_id || row.id;
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPulling, setBulkPulling] = useState(false);

  // When the page or filter changes, prune selections to rows still
  // present so the count never drifts (mirrors the WP Posts grid).
  useEffect(() => {
    if (!selected.size) return;
    const present = new Set(data.map(rowKeyOf));
    let changed = false;
    const next = new Set();
    selected.forEach(id => {
      if (present.has(id)) next.add(id);
      else changed = true;
    });
    if (changed) setSelected(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const allOnPageSelected = data.length > 0 && data.every(r => selected.has(rowKeyOf(r)));
  const someOnPageSelected = !allOnPageSelected && data.some(r => selected.has(rowKeyOf(r)));

  const toggleRow = (row) => {
    setSelected(prev => {
      const k = rowKeyOf(row);
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const toggleAllOnPage = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) data.forEach(r => next.delete(rowKeyOf(r)));
      else data.forEach(r => next.add(rowKeyOf(r)));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const onBulkPullClick = async () => {
    if (!onBulkPull || selected.size === 0) return;
    const picks = data.filter(r => selected.has(rowKeyOf(r)));
    setBulkPulling(true);
    try {
      await onBulkPull(picks);
      clearSelection();
    } catch (e) {
      window.alert(e?.response?.data?.detail || e?.message || 'Bulk pull failed');
    } finally {
      setBulkPulling(false);
    }
  };

  const onBulkDeleteClick = async () => {
    if (!onBulkDelete || selected.size === 0) return;
    const picks = data.filter(r => selected.has(rowKeyOf(r)));
    const carriedOver = selected.size - picks.length;
    const msg =
      `Delete ${selected.size} selected ${entityLabel || 'row'}${selected.size === 1 ? '' : 's'}?` +
      (carriedOver > 0
        ? `\n\n(${carriedOver} of the selected rows is on another page and will also be deleted.)`
        : '') +
      `\n\nThis cannot be undone. If the entity is mirrored to WordPress, the deletion will also propagate via the next sync sweep.`;
    if (!window.confirm(msg)) return;
    setBulkBusy(true);
    try {
      await onBulkDelete(Array.from(selected));
      clearSelection();
    } catch (e) {
      window.alert(e?.response?.data?.detail || e?.message || 'Bulk delete failed');
    } finally {
      setBulkBusy(false);
    }
  };

  // Inline-edit is enabled when EITHER a per-cell or a bulk save
  // handler is wired. Existing call sites passing `onCellSave` keep
  // working; new code should prefer `onBulkSave`.
  const inlineEditEnabled = !!(onCellSave || onBulkSave);

  return (
    <div className="space-y-3" data-testid="data-table">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder={`Search ${entityLabel || 'items'}...`} value={searchTerm || ''} onChange={e => onSearchChange?.(e.target.value)} className="pl-9 bg-slate-800 border-slate-600 text-slate-100 h-9 placeholder:text-slate-400" data-testid="table-search" />
        </div>
        {inlineFilters && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="table-inline-filters">
            {inlineFilters}
          </div>
        )}
        {onNew && (
          <Button onClick={onNew} className="bg-amber-600 hover:bg-amber-700 text-white h-9" size="sm" data-testid="new-entity-btn">
            <Plus className="h-4 w-4 mr-1" /> New {entityLabel}
          </Button>
        )}
      </div>

      {/* Inline-edit hint banner — visible exactly once per session via
          a no-op tooltip on the search bar would be subtler, but a
          tiny inline note keeps the discoverability obvious. */}
      {inlineEditEnabled && (
        <div className="text-[11px] text-slate-400 flex items-center gap-1.5 -mt-1" data-testid="inline-edit-hint">
          <Edit2 className="h-3 w-3 text-amber-400" />
          <span>Click any cell to edit. Enter queues the change; the floating <strong className="text-amber-300">Save</strong> button at the bottom-right flushes a burst to WordPress. Esc cancels.</span>
        </div>
      )}

      {/* Contextual bulk-action bar — visible whenever something is selected. */}
      {selectable && selected.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-100 text-xs"
          data-testid="bulk-action-bar"
        >
          <div className="flex items-center gap-2">
            <Check className="h-3.5 w-3.5" />
            <span data-testid="bulk-selected-count">
              {selected.size} {selected.size === 1 ? 'row' : 'rows'} selected
            </span>
            {someOnPageSelected && (
              <button
                onClick={toggleAllOnPage}
                className="underline text-amber-200 hover:text-white"
                data-testid="bulk-select-all-page"
              >
                Select all {data.length} on this page
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
              disabled={bulkBusy || bulkPulling}
              className="h-7 text-xs border-amber-500/50 text-amber-200 hover:text-white"
              data-testid="bulk-clear"
            >
              Clear
            </Button>
            {onBulkPull && (
              <Button
                size="sm"
                onClick={onBulkPullClick}
                disabled={bulkBusy || bulkPulling}
                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="bulk-pull-btn"
              >
                {bulkPulling
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : <Download className="h-3 w-3 mr-1" />}
                Pull {selected.size} from WP
              </Button>
            )}
            {onBulkDelete && (
            <Button
              size="sm"
              onClick={onBulkDeleteClick}
              disabled={bulkBusy || bulkPulling}
              className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-delete-btn"
            >
              {bulkBusy
                ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                : <Trash2 className="h-3 w-3 mr-1" />}
              Delete {selected.size}
            </Button>)}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-slate-600 overflow-hidden">
        <div className="overflow-x-auto overflow-y-auto max-h-[68vh]" data-testid="table-scroll">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-[2]">
              <tr className="bg-slate-700/95 border-b border-slate-600 backdrop-blur">
                {selectable && (
                  <th className="px-3 py-1.5 w-10 text-left">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      ref={el => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                      onChange={toggleAllOnPage}
                      className="accent-amber-500 cursor-pointer"
                      data-testid="bulk-select-all-header"
                      title={allOnPageSelected ? 'Deselect all on page' : 'Select all on page'}
                    />
                  </th>
                )}
                {columns.map(col => (
                  <th key={col.key} className="px-2 py-1.5 text-left text-xs font-semibold text-slate-200 uppercase tracking-wider whitespace-nowrap">
                    {col.sortable ? (
                      <button
                        className="flex items-center gap-1 hover:text-white transition-colors"
                        onClick={() => onSort?.(col.key)}
                        data-testid={`col-sort-${col.key}`}
                      >
                        <Tooltip text={col.tooltip}>{col.label}</Tooltip>
                        {sortKey === col.key
                          ? <span className="text-amber-300 font-mono text-[10px]">{sortDir === 'desc' ? '▼' : '▲'}</span>
                          : <ArrowUpDown className="h-3 w-3 opacity-60" />}
                      </button>
                    ) : <Tooltip text={col.tooltip}>{col.label}</Tooltip>}
                  </th>
                ))}
                <th className="px-2 py-1.5 text-right text-xs font-semibold text-slate-200 uppercase tracking-wider sticky right-0 bg-slate-700/95 z-10">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {loading ? (
                <tr><td colSpan={columns.length + 1 + (selectable ? 1 : 0)} className="px-3 py-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500 mx-auto" /></td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={columns.length + 1 + (selectable ? 1 : 0)} className="px-3 py-8 text-center text-slate-400">
                  No {entityLabel || 'items'} found.
                  {onNew && <Button variant="link" onClick={onNew} className="text-amber-400 ml-2">Create one</Button>}
                </td></tr>
              ) : data.map((row, i) => {
                // OL `ol_id` (UUID) is the canonical unique key. The WP
                // `id` field may be duplicated across rows (legacy
                // schedules ingested twice from prior WP targets), and
                // using a non-unique React key causes the previous
                // tab's DOM to persist when navigating between entities.
                // Always key by ol_id first, fall back to id+index.
                const rowKey = row.ol_id || `${row.id}-${i}`;
                // Collect every error keyed at this row so we can render
                // a row-bottom bar listing them all (per spec).
                const rowErrors = Object.entries(errorMap)
                  .filter(([k]) => k.startsWith(`${row.id}:`))
                  .map(([k, msg]) => {
                    const colKey = k.slice(String(row.id).length + 1);
                    const col = columns.find(c => c.key === colKey);
                    return { colKey, label: col?.label || colKey, msg };
                  });
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={`hover:bg-slate-700/30 transition-colors ${selectable && selected.has(rowKeyOf(row)) ? 'bg-amber-500/10' : ''}`}
                      data-testid={`table-row-${rowKey}`}
                    >
                      {selectable && (
                        <td className="px-3 py-1 w-10">
                          <input
                            type="checkbox"
                            checked={selected.has(rowKeyOf(row))}
                            onChange={() => toggleRow(row)}
                            onClick={(e) => e.stopPropagation()}
                            className="accent-amber-500 cursor-pointer"
                            data-testid={`row-select-${row.id}`}
                            title="Select for bulk action"
                          />
                        </td>
                      )}
                      {columns.map(col => {
                        const cellKey = `${row.id}:${col.key}`;
                        const error = errorMap[cellKey];
                        const useToggle = (col.type === 'boolean' || col.type === 'homeaway') && inlineEditEnabled;
                        return (
                          <td key={col.key} className="px-2 py-1 text-xs text-white whitespace-nowrap">
                            {inlineEditEnabled ? (
                              useToggle ? (
                                <ToggleCell
                                  col={col} row={row}
                                  onToggle={(val) => queueChange(row, col.key, val)}
                                  queuedValue={getQueuedValue(row, col.key)}
                                  errorKey={error}
                                />
                              ) : (
                                <EditableCell
                                  col={col} row={row}
                                  entities={entities} siteTimezone={siteTimezone}
                                  onQueue={queueChange}
                                  getQueuedValue={getQueuedValue}
                                  errorKey={error}
                                  onOpenModal={onRowEdit}
                                  archiveReadOnly={archiveReadOnly}
                                />
                              )
                            ) : (
                              <CellValue col={col} row={row} entities={entities} siteTimezone={siteTimezone} />
                            )}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1 text-right sticky right-0 bg-slate-800 z-[1]" style={{ boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.04)' }}>
                        <div className="flex items-center justify-end gap-1">
                          {archiveReadOnly && (row._archived === true || (row._source && row._source !== 'current')) ? (
                            <span className="text-[10px] uppercase font-bold text-amber-400/70 bg-amber-500/10 px-1.5 py-0.5 rounded" title="Archived season record — read-only. Edit current staff only." data-testid={`row-archive-${row.id}`}>Archive</span>
                          ) : (
                            <>
                              {onRowPull && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-emerald-400 shrink-0" onClick={e => { e.stopPropagation(); handleRowPull(row); }} disabled={pullingId === row.id} title="Pull this row's latest values from WordPress (scoped — no bulk seeding)" data-testid={`row-pull-${row.id}`}>
                                {pullingId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                              </Button>)}
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-300 hover:text-white shrink-0" onClick={e => { e.stopPropagation(); onRowEdit?.(row); }} title="Open full editor" data-testid={`row-edit-${row.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                              {onRowDelete && <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-400 shrink-0" onClick={e => { e.stopPropagation(); onRowDelete?.(row); }} title="Delete" data-testid={`row-del-${row.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {rowErrors.length > 0 && (
                      <tr className="bg-red-900/20 border-t border-red-500/30" data-testid={`row-errors-${row.id}`}>
                        <td colSpan={columns.length + 1 + (selectable ? 1 : 0)} className="px-3 py-1.5">
                          <div className="flex items-start gap-2 text-[11px] text-red-200">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-red-400" />
                            <div className="flex-1 flex flex-wrap gap-x-4 gap-y-0.5">
                              {rowErrors.map(re => (
                                <span key={re.colKey}>
                                  <strong className="text-red-300">{re.label}:</strong> {re.msg}
                                </span>
                              ))}
                            </div>
                            <button onClick={() => setErrorMap(prev => {
                              const next = { ...prev };
                              rowErrors.forEach(re => delete next[`${row.id}:${re.colKey}`]);
                              return next;
                            })} className="text-red-300 hover:text-red-100 underline text-[10px]">dismiss</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-slate-300">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" className="h-7 w-7 border-slate-600 text-slate-200" disabled={page <= 1} onClick={() => onPageChange?.(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-7 w-7 border-slate-600 text-slate-200" disabled={page >= totalPages} onClick={() => onPageChange?.(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {/* Floating Save bar — anchored bottom-right of the viewport so
          the user can save a burst of edits without scrolling back to
          a button at the bottom of a 200-row grid. Visible whenever
          there's at least one queued change. */}
      {pendingChanges.size > 0 && (
        <div
          className="fixed bottom-4 right-4 z-[55] flex items-center gap-3 px-4 py-2.5 rounded-lg shadow-xl bg-slate-900/95 border border-amber-500/60 backdrop-blur"
          data-testid="floating-save-bar"
        >
          <div className="flex items-center gap-2 text-amber-200">
            <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-xs font-medium" data-testid="floating-save-count">
              {pendingChanges.size} unsaved {pendingChanges.size === 1 ? 'change' : 'changes'}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={discardChanges}
            disabled={bulkSaving}
            className="h-7 text-xs border-slate-600 text-slate-300 hover:text-white"
            data-testid="floating-save-discard"
          >
            Discard
          </Button>
          <Button
            size="sm"
            onClick={saveChanges}
            disabled={bulkSaving}
            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="floating-save-btn"
          >
            {bulkSaving
              ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              : <Check className="h-3 w-3 mr-1" />}
            Save {pendingChanges.size}
          </Button>
        </div>
      )}
    </div>
  );
}
